import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchIndividualRehabPlanTwo } from "../../../store/slices/individualRehabPlanTwoSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

const cb = (v) => (v ? "☑" : "☐");
const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const STORAGE_URL = "https://trh-backend.clouxiplexi.com/storage/";
const sigSrc = (v) => (!v ? null : v.startsWith("data:") ? v : `${STORAGE_URL}${v}`);

export default function IndividualRehabPlanTwoPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentIndividualRehabPlanTwo, isLoading, error } = useSelector(
    (s) => s.individualRehabPlanTwo
  );

  useEffect(() => {
    if (id) dispatch(fetchIndividualRehabPlanTwo(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentIndividualRehabPlanTwo) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(t);
        window.onafterprint = null;
      };
    }
  }, [currentIndividualRehabPlanTwo, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading Individual Rehabilitation Plan (2) print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentIndividualRehabPlanTwo) return null;

  const d = currentIndividualRehabPlanTwo;
  const recoveryCapitals = d.recovery_capitals || [];
  const strengthsGrowths = d.strengths_growths || [];
  const objectives = d.objectives || [];
  const selfReviews = d.self_reviews || [];

  const SectionTitle = ({ children }) => <div className="section-title">{children}</div>;

  return (
    <>
      <style jsx global>{`
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
        @media print {
          @page { size: A4; margin: 14mm 12mm; }
          body { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .section, table { page-break-inside: avoid; }
          /* Force Chrome/Safari to keep table borders & header shading in print */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          table { border-collapse: collapse !important; }
          table, th, td { border: 1px solid #000 !important; }
          th { background: #f0f0f0 !important; }
          .section-title { border: 1px solid #000 !important; background: #e9e9e9 !important; }
          .notes-box, .consent { border: 1px solid #000 !important; }
          .lbl { background: #f7f7f7 !important; }
        }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .irp2-print, .irp2-print * { font-family: "Times New Roman", Times, serif; color: #000; }
        .irp2-print { width: 100%; max-width: 820px; margin: 0 auto; padding: 18px; font-size: 10.5pt; line-height: 1.35; }
        .header-logo { text-align: center; margin-bottom: 6px; }
        .header-logo img { height: 62px; }
        .doc-title { text-align: center; font-size: 17pt; font-weight: bold; text-transform: uppercase; margin: 0; letter-spacing: 1px; }
        .doc-sub { text-align: center; font-size: 11pt; font-weight: bold; margin: 2px 0 10px; }
        .section { margin-bottom: 16px; }
        .section-title { font-size: 11pt; font-weight: bold; background: #e9e9e9; border: 1px solid #000; padding: 4px 8px; text-transform: uppercase; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
        td, th { border: 1px solid #000; padding: 5px 7px; font-size: 9.5pt; vertical-align: top; }
        th { background: #f0f0f0; text-align: left; }
        .lbl { font-weight: bold; background: #f7f7f7; width: 22%; }
        .chk-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 16px; margin-bottom: 6px; }
        .chk-grid.four { grid-template-columns: repeat(4, 1fr); }
        .chk { font-size: 9.7pt; padding: 1px 0; }
        .chk .box { font-size: 11pt; }
        .notes-box { border: 1px solid #000; min-height: 42px; padding: 5px 7px; white-space: pre-wrap; font-size: 9.5pt; margin-bottom: 6px; }
        .consent { border: 1px solid #000; padding: 6px 8px; font-size: 9pt; font-style: italic; background: #fafafa; }
        .footer-badges { text-align: center; font-size: 8.5pt; margin-top: 12px; border-top: 1px solid #000; padding-top: 6px; }
        .signoff td { height: 34px; }
      `}</style>

      <PrintWrapper>
        <div className="irp2-print">
        <div className="header-logo">
          <img src={logo} alt="The Recovery House Logo" />
        </div>
        <h1 className="doc-title">Individual Rehabilitation Plan</h1>
        <div className="doc-sub">Client-Centred SMART Rehabilitation Plan (CARF-Aligned)</div>

        {/* SECTION 1 */}
        <div className="section">
          <SectionTitle>Section 1 | Client Identification &amp; Plan Details</SectionTitle>
          <table>
            <tbody>
              <tr><td className="lbl">Client Name</td><td>{val(d.client_name)}</td><td className="lbl">Date of Plan</td><td>{fmt(d.date_of_plan)}</td></tr>
              <tr><td className="lbl">Date of Birth</td><td>{fmt(d.date_of_birth)}</td><td className="lbl">Plan Period</td><td>{val(d.plan_period)}</td></tr>
              <tr><td className="lbl">Diagnosis (if applicable)</td><td>{val(d.diagnosis)}</td><td className="lbl">Rehab Goal #</td><td>{val(d.rehab_goal_no)}</td></tr>
              <tr><td className="lbl">PRP Practitioner</td><td>{val(d.prp_practitioner)}</td><td className="lbl">Program / Site</td><td>{val(d.program_site)}</td></tr>
            </tbody>
          </table>
          <div style={{ fontSize: "8.5pt", fontStyle: "italic" }}>
            CARF Standard: All identifying information is treated as confidential and shared only with informed client consent.
          </div>
        </div>

        {/* SECTION 2 */}
        <div className="section">
          <SectionTitle>Section 2 | Environment of Choice (Domain of Focus)</SectionTitle>
          <div style={{ fontSize: "8.8pt", marginBottom: "4px" }}>Select the primary environment the client wishes to focus on (check one):</div>
          <div className="chk-grid four">
            <div className="chk"><span className="box">{cb(d.env_living)}</span> Living</div>
            <div className="chk"><span className="box">{cb(d.env_learning)}</span> Learning</div>
            <div className="chk"><span className="box">{cb(d.env_working)}</span> Working</div>
            <div className="chk"><span className="box">{cb(d.env_socializing)}</span> Socializing</div>
          </div>
          <div style={{ fontWeight: "bold", fontSize: "9pt", margin: "4px 0 2px" }}>Preferred Environment Details:</div>
          <div className="notes-box">{d.preferred_environment_details || ""}</div>
        </div>

        {/* SECTION 3 */}
        <div className="section">
          <SectionTitle>Section 3 | Collaborative Development &amp; Informed Consent</SectionTitle>
          <div style={{ fontSize: "8.8pt", marginBottom: "4px" }}>This plan was developed collaboratively with (select all that apply):</div>
          <div className="chk-grid">
            <div className="chk"><span className="box">{cb(d.collab_client_self)}</span> Client (Self)</div>
            <div className="chk"><span className="box">{cb(d.collab_prp)}</span> PRP Practitioner</div>
            <div className="chk"><span className="box">{cb(d.collab_family_caregiver)}</span> Family / Caregiver</div>
            <div className="chk"><span className="box">{cb(d.collab_interdisciplinary)}</span> Interdisciplinary Team</div>
            <div className="chk"><span className="box">{cb(d.collab_other)}</span> Other</div>
          </div>
          <div style={{ fontWeight: "bold", fontSize: "9pt", margin: "4px 0 2px" }}>Client's Voice &amp; Preferences:</div>
          <div className="notes-box">{d.clients_voice || ""}</div>
          <div className="consent">
            <strong>Client Consent Statement:</strong> I, the undersigned, confirm that I have been actively involved in developing this
            Individual Rehabilitation Plan. The goals reflect my personal recovery aspirations and have been explained to me in language I
            understand. I consent to the use of this plan to guide my rehabilitation and agree to review progress as outlined below. I
            understand I may withdraw consent or request revisions at any time.
          </div>
        </div>

        {/* SECTION 4 */}
        <div className="section">
          <SectionTitle>Section 4 | Recovery Capital Assessment</SectionTitle>
          <table>
            <thead>
              <tr><th>Personal Capital</th><th>Social / Family</th><th>Community</th><th>Financial / Housing</th></tr>
            </thead>
            <tbody>
              {recoveryCapitals.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center" }}>—</td></tr>}
              {recoveryCapitals.map((r, i) => (
                <tr key={r.id || i}>
                  <td>{val(r.personal_capital)}</td>
                  <td>{val(r.social_family)}</td>
                  <td>{val(r.community)}</td>
                  <td>{val(r.financial_housing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* SECTION 5 */}
        <div className="section">
          <SectionTitle>Section 5 | Strengths &amp; Areas for Growth (PROs)</SectionTitle>
          <table>
            <thead>
              <tr><th>Strengths (Skills &amp; Resources Possessed)</th><th>Areas for Growth (Skills &amp; Resources Needed)</th></tr>
            </thead>
            <tbody>
              {strengthsGrowths.length === 0 && <tr><td colSpan={2} style={{ textAlign: "center" }}>—</td></tr>}
              {strengthsGrowths.map((r, i) => (
                <tr key={r.id || i}>
                  <td>{val(r.strengths)}</td>
                  <td>{val(r.areas_for_growth)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* SECTION 6 */}
        <div className="section">
          <SectionTitle>Section 6 | Client-Centred SMART Goal Development</SectionTitle>
          {(d.smart_goals && d.smart_goals.length > 0) ? (
            d.smart_goals.map((g, i) => (
              <div key={g.id || i} style={{ marginBottom: "12px" }}>
                <div style={{ fontWeight: "bold", fontSize: "9pt", background: "#f0f0f0", padding: "3px 6px", border: "1px solid #000" }}>
                  Goal {i + 1}
                  {g.start_date || g.end_date ? `  (Start: ${fmt(g.start_date)}  |  End: ${fmt(g.end_date)})` : ""}
                </div>
                <div style={{ fontWeight: "bold", fontSize: "9pt", margin: "3px 0 2px" }}>Rehabilitation Goal Statement:</div>
                <div className="notes-box">{g.rehab_goal_statement || ""}</div>
                <table>
                  <thead>
                    <tr><th style={{ width: "12%" }}>SMART</th><th style={{ width: "30%" }}>Goal Component</th><th>Client Input</th><th>PRP Notes</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>S – Specific</td><td>What exactly will be achieved? (Who, What, Where, When, Why)</td><td>{val(g.smart_specific_client)}</td><td>{val(g.smart_specific_prp)}</td></tr>
                    <tr><td>M – Measurable</td><td>How will progress be measured? (Frequency, amount, milestones)</td><td>{val(g.smart_measurable_client)}</td><td>{val(g.smart_measurable_prp)}</td></tr>
                    <tr><td>A – Achievable</td><td>Is the goal realistic given strengths and resources?</td><td>{val(g.smart_achievable_client)}</td><td>{val(g.smart_achievable_prp)}</td></tr>
                    <tr><td>R – Relevant</td><td>Is the goal meaningful to the client's recovery journey?</td><td>{val(g.smart_relevant_client)}</td><td>{val(g.smart_relevant_prp)}</td></tr>
                    <tr><td>T – Time-bound</td><td>What is the target completion date?</td><td>{val(g.smart_timebound_client)}</td><td>{val(g.smart_timebound_prp)}</td></tr>
                  </tbody>
                </table>
              </div>
            ))
          ) : (
            <div style={{ fontStyle: "italic", color: "#888" }}>No SMART goals recorded.</div>
          )}
        </div>

        {/* SECTION 7 */}
        <div className="section">
          <SectionTitle>Section 7 | Objectives, Action Steps &amp; Environment</SectionTitle>
          <div style={{ fontSize: "8.5pt", marginBottom: "4px" }}>Status codes: ✔ Achieved &nbsp; ⟳ In Progress &nbsp; ✕ Discontinued &nbsp; ↻ Modified</div>
          <table>
            <thead>
              <tr>
                <th>Objective</th><th>Action Steps</th><th style={{ width: "14%" }}>Environment</th>
                <th style={{ width: "13%" }}>Frequency &amp; Duration</th><th style={{ width: "11%" }}>Target Date</th><th style={{ width: "11%" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {objectives.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center" }}>—</td></tr>}
              {objectives.map((r, i) => (
                <tr key={r.id || i}>
                  <td>{val(r.objective)}</td>
                  <td>{val(r.action_steps)}</td>
                  <td>{val(r.environment_of_choice)}</td>
                  <td>{val(r.frequency_duration)}</td>
                  <td>{fmt(r.target_date)}</td>
                  <td>{val(r.obj_status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* SECTION 8 */}
        <div className="section">
          <SectionTitle>Section 8 | Progress Review Schedule</SectionTitle>
          <table>
            <thead>
              <tr><th style={{ width: "13%" }}>Review Period</th><th>Focus / Activity</th><th style={{ width: "15%" }}>Target Date</th><th style={{ width: "26%" }}>Outcome / Notes</th></tr>
            </thead>
            <tbody>
              <tr><td>Twice Weekly</td><td>Progress check-in with PRP practitioner</td><td>{fmt(d.review_twice_weekly_date)}</td><td>{val(d.review_twice_weekly_notes)}</td></tr>
              <tr><td>Weekly</td><td>Full goal review — SMART objectives assessed</td><td>{fmt(d.review_weekly_date)}</td><td>{val(d.review_weekly_notes)}</td></tr>
              <tr><td>Monthly</td><td>Formal progress evaluation — barriers &amp; supports</td><td>{fmt(d.review_monthly_date)}</td><td>{val(d.review_monthly_notes)}</td></tr>
              <tr><td>Quarterly</td><td>Comprehensive plan review — revise if needed</td><td>{fmt(d.review_quarterly_date)}</td><td>{val(d.review_quarterly_notes)}</td></tr>
              <tr><td>6-Monthly</td><td>Outcome decision — Continue / Achieve / Modify / Discontinue</td><td>{fmt(d.review_6monthly_date)}</td><td>{val(d.review_6monthly_notes)}</td></tr>
            </tbody>
          </table>
        </div>

        {/* SECTION 9 */}
        <div className="section">
          <SectionTitle>Section 9 | Client Progress Self-Review Log</SectionTitle>
          <table>
            <thead>
              <tr><th style={{ width: "13%" }}>Date</th><th>Client Self-Assessment</th><th>PRP Observation</th><th>Next Steps</th></tr>
            </thead>
            <tbody>
              {selfReviews.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center" }}>—</td></tr>}
              {selfReviews.map((r, i) => (
                <tr key={r.id || i}>
                  <td>{fmt(r.review_date)}</td>
                  <td>{val(r.client_self_assessment)}</td>
                  <td>{val(r.prp_observation)}</td>
                  <td>{val(r.next_steps)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* SECTION 10 */}
        <div className="section">
          <SectionTitle>Section 10 | Stage-Based Rehabilitation Model</SectionTitle>
          <div style={{ fontSize: "8.8pt", marginBottom: "4px" }}>Minimum 60% Goal Achievement required for stage advancement.</div>
          <table>
            <thead>
              <tr><th>Stage</th><th>Role</th><th>Activities</th><th style={{ width: "16%" }}>Date Achieved</th></tr>
            </thead>
            <tbody>
              <tr><td>Stage 1</td><td>Client (Skill Building)</td><td>Structured program participation, core life skills</td><td>{fmt(d.stage1_date_achieved)}</td></tr>
              <tr><td>Stage 2</td><td>Observer</td><td>Observes sessions, builds insight, reduces stigma</td><td>{fmt(d.stage2_date_achieved)}</td></tr>
              <tr><td>Stage 3</td><td>Co-Facilitator</td><td>Assists sessions, applies skills</td><td>{fmt(d.stage3_date_achieved)}</td></tr>
              <tr><td>Stage 4</td><td>Facilitator</td><td>Leads sessions independently under PRP supervision</td><td>{fmt(d.stage4_date_achieved)}</td></tr>
            </tbody>
          </table>
          <table>
            <tbody>
              <tr><td className="lbl">Duration in Program</td><td>{val(d.duration_in_program)}</td></tr>
            </tbody>
          </table>
        </div>

        {/* SECTION 11 */}
        <div className="section">
          <SectionTitle>Section 11 | 6-Month Outcome Decision</SectionTitle>
          <table>
            <tbody>
              <tr><td className="lbl">Review Date</td><td colSpan={3}>{fmt(d.outcome_review_date)}</td></tr>
              <tr>
                <td className="lbl">Goal Status Decision</td>
                <td colSpan={3}>
                  <span style={{ marginRight: 14 }}>{cb(d.goal_status_continue)} Continue</span>
                  <span style={{ marginRight: 14 }}>{cb(d.goal_status_achieved)} Achieved</span>
                  <span style={{ marginRight: 14 }}>{cb(d.goal_status_modify)} Modify</span>
                  <span>{cb(d.goal_status_discontinue)} Discontinue</span>
                </td>
              </tr>
              <tr><td className="lbl">Rationale</td><td colSpan={3}>{val(d.outcome_rationale)}</td></tr>
              <tr><td className="lbl">New Goal (if applicable)</td><td colSpan={3}>{val(d.new_goal)}</td></tr>
            </tbody>
          </table>
        </div>

        {/* SECTION 12 */}
        <div className="section">
          <SectionTitle>Section 12 | Signatures &amp; Authorization</SectionTitle>
          <div style={{ fontSize: "8.8pt", marginBottom: "5px" }}>
            Signatures confirm collaborative participation and informed consent. Family / caregiver signature is obtained with client consent.
          </div>
          <table className="signoff">
            <thead>
              <tr><th style={{ width: "34%" }}>Participant</th><th>Signature</th><th style={{ width: "16%" }}>Date</th><th style={{ width: "22%" }}>Relationship / Role</th></tr>
            </thead>
            <tbody>
              {[
                ["Client", d.client_signature, d.client_sign_date, "Self"],
                ["Psychiatric Rehabilitation Practitioner (PRP)", d.prp_signature, d.prp_sign_date, "Lead Practitioner"],
                ["Family Member / Caregiver", d.family_signature, d.family_sign_date, d.family_relationship || ""],
                [`Other${d.other_specify ? " (" + d.other_specify + ")" : " (specify)"}`, d.other_signature, d.other_sign_date, d.other_specify || ""],
              ].map(([role, sig, date, rel]) => (
                <tr key={role}>
                  <td>{role}</td>
                  <td>{sigSrc(sig) ? <img src={sigSrc(sig)} alt={`${role} signature`} style={{ height: 38, maxWidth: 190, objectFit: "contain" }} /> : ""}</td>
                  <td>{date ? fmt(date) : ""}</td>
                  <td>{rel || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: "9pt", marginTop: "6px" }}>
            Document version: <strong>{val(d.document_version)}</strong> &nbsp;&nbsp;
            Next scheduled review: <strong>{fmt(d.next_scheduled_review)}</strong> &nbsp;&nbsp;
            Stored in: {cb(d.stored_electronic)} Electronic Record &nbsp; {cb(d.stored_paper)} Paper File &nbsp; {cb(d.stored_both)} Both
          </div>
        </div>

        <div className="footer-badges">CONFIDENTIAL — For Authorised Clinical Use Only</div>
      </div>
      </PrintWrapper>
    </>
  );
}
